import { Navigate, useLocation } from 'react-router-dom';
import { useMembersAuth } from '@/contexts/MembersAuthContext';
import { Loader2 } from 'lucide-react';

interface ProtectedMemberRouteProps {
  children: React.ReactNode;
  requireVIP?: boolean; // If true, requires VIP subscription
  requireOnboarding?: boolean; // If true, requires onboarding to be completed
}

export function ProtectedMemberRoute({ 
  children, 
  requireVIP = false,
  requireOnboarding = true 
}: ProtectedMemberRouteProps) {
  const { user, isLoading, isSubscribed, isAdmin, profile } = useMembersAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">A verificar acesso...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to members login
  if (!user) {
    return <Navigate to="/members/auth" state={{ from: location }} replace />;
  }

  // Check if onboarding is required and not completed
  // Skip this check for the onboarding page itself and if requireOnboarding is false
  if (requireOnboarding && profile && !profile.onboarding_completed && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  // If VIP is required, check subscription
  if (requireVIP) {
    if (isAdmin || isSubscribed) {
      return <>{children}</>;
    }
    // Not subscribed and not admin - redirect to upgrade page
    return <Navigate to="/members/upgrade" state={{ needsSubscription: true }} replace />;
  }

  // For non-VIP routes, any authenticated user can access
  return <>{children}</>;
}

// New component for routes that require VIP subscription
export function ProtectedVIPRoute({ children }: { children: React.ReactNode }) {
  return <ProtectedMemberRoute requireVIP={true}>{children}</ProtectedMemberRoute>;
}

// Component for protected routes that don't require onboarding (like the onboarding page itself)
export function ProtectedAuthRoute({ children }: { children: React.ReactNode }) {
  return <ProtectedMemberRoute requireOnboarding={false}>{children}</ProtectedMemberRoute>;
}

// Component for admin-only routes (requires Members Auth + admin role)
export function ProtectedAdminMemberRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, isAdmin } = useMembersAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">A verificar acesso administrativo...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to members login
  if (!user) {
    return <Navigate to="/members/auth" state={{ from: location }} replace />;
  }

  // Not admin - redirect to members dashboard
  if (!isAdmin) {
    return <Navigate to="/members/dashboard" replace />;
  }

  return <>{children}</>;
}
